import handleError from '@fuse/core/errorHandler';
import { Button, InputLabel, MenuItem, Select, TextField, FormControl } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TemplateApproverSelector from 'src/app/main/apps/templates-constructor/components/TemplateApproverSelector';
import { useCreateTagMutation, useEditTagMutation } from 'src/app/services/claim/claimApi';
import './tags.css';

const defaultValues = {
  name: '',
  type: '',
  value: '',
  fio: '',
};

const ALLOWED_PATTERN = /^[\p{L}\p{N}]/u;

const TagsForm = ({ setOpen, refetch, tag }) => {
  const { t } = useTranslation('translations');
  const [selectTypeId, setSelectTypeId] = useState(tag?.type ?? '');
  const [getStaffId, setGetStaffId] = useState(null);
  const [createTag, { isLoading: isCreateLoading }] = useCreateTagMutation();
  const [updateTag, { isLoading: isUpdateLoading }] = useEditTagMutation();
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
  } = useForm({
    mode: 'onChange',
    defaultValues: tag
      ? {
          name: tag.name || '',
          type: tag.type ?? '',
          value: tag.type === 1 ? tag.value ?? 0 : tag.value ?? '',
          fio: tag.fio || '',
        }
      : defaultValues,
  });

  useEffect(() => {
    if (tag) {
      reset({
        name: tag.name || '',
        type: tag.type ?? '',
        value: tag.type === 1 ? tag.value ?? 0 : tag.value ?? '',
        fio: tag.fio || '',
      });
      setSelectTypeId(tag.type ?? '');
      setGetStaffId(tag.staffId ? { id: tag.staffId } : null);
    } else {
      reset(defaultValues);
      setSelectTypeId('');
      setGetStaffId(null);
    }
  }, [tag, reset]);

  const handleClose = () => {
    setOpen(false);
    reset(defaultValues);
  };

  const trimObject = (obj) =>
    Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
    );

  const makeTextRules = (isRequired = true) => ({
    ...(isRequired ? { required: t('RequiredField') || 'Required' } : {}),
    validate: (value) => {
      const v = (value ?? '').trim();
      if (isRequired && !v) return t('EmptyInput') || 'This field cannot be empty';
      if (v && !ALLOWED_PATTERN.test(v)) {
        return (
          t('SpecialCharactersNotAllowed') ||
          "Only letters, numbers, spaces and - . , _ ( ) / & : + ' are allowed"
        );
      }
      return true;
    },
  });

  const onChangeTrimStart = (fieldOnChange) => (e) => {
    const val = e.target.value;
    fieldOnChange(typeof val === 'string' ? val.trimStart() : val);
  };

  const onSubmit = async (data) => {
    try {
      const trimmed = trimObject(data);

      const payload = {
        ...(trimmed.name && { name: trimmed.name }),
        ...(selectTypeId !== '' && { type: selectTypeId }),
        ...(trimmed.fio && { fio: trimmed.fio }),
      };

      if (selectTypeId === 3) {
        if (getStaffId?.id) payload.value = getStaffId.id;
      } else if (selectTypeId === 1) {
        const num = trimmed.value === '' || trimmed.value == null ? 0 : Number(trimmed.value);
        payload.value = Number.isFinite(num) ? num : 0;
      } else if (selectTypeId === 0) {
        if (trimmed.value !== '') payload.value = trimmed.value;
      }

      const promise = tag ? updateTag({ ...payload, id: tag.id }) : createTag(payload);

      await promise.unwrap();
      await refetch?.();
      handleClose();
    } catch (err) {
      handleError(err);
    }
  };

  const SelectTypes = [
    { id: 0, name: t('Text') },
    { id: 1, name: t('Number') },
    { id: 3, name: t('Employee') },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-16 pt-10 max-w-7xl">
      <Controller
        name="name"
        control={control}
        // rules={makeTextRules(true)}
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ''}
            label={t('Title')}
            error={!!errors.name}
            helperText={errors.name?.message}
            fullWidth
            variant="outlined"
            onChange={onChangeTrimStart(field.onChange)}
          />
        )}
      />
      <FormControl fullWidth>
        <InputLabel id="select-type-label">{t('Type')}</InputLabel>
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              labelId="select-type-label"
              id="select-type"
              label={t('Type')}
              value={field.value === 0 || field.value === 1 || field.value === 3 ? field.value : ''}
              onChange={(e) => {
                const val = e.target.value;
                field.onChange(val);
                setSelectTypeId(val);
                if (val === 1) {
                  setValue('value', 0, { shouldValidate: true, shouldDirty: true });
                } else {
                  setValue('value', '', { shouldValidate: true, shouldDirty: true });
                }
                if (val !== 3) setGetStaffId(null);
              }}
              displayEmpty
            >
              {SelectTypes.map((type) => (
                <MenuItem value={type.id} key={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          )}
        />
        <div className="mt-12">
          {selectTypeId === 0 && (
            <Controller
              name="value"
              control={control}
              // rules={makeTextRules(false)}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  InputLabelProps={{ shrink: true }}
                  label={t('Text')}
                  fullWidth
                  variant="outlined"
                  error={!!errors.value}
                  helperText={errors.value?.message}
                  onChange={onChangeTrimStart(field.onChange)}
                />
              )}
            />
          )}
          {selectTypeId === 1 && (
            <Controller
              name="value"
              control={control}
              rules={{
                min: { value: 0, message: t('MustBeZeroOrMore') || 'Must be 0 or more' },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? 0}
                  InputLabelProps={{ shrink: true }}
                  label={t('Number')}
                  inputProps={{ min: 0, className: 'no-spinner' }}
                  error={!!errors.value}
                  helperText={errors.value?.message}
                  type="number"
                  fullWidth
                  variant="outlined"
                />
              )}
            />
          )}
          {selectTypeId === 3 && (
            <TemplateApproverSelector
              extraStyle={false}
              onChange={(staff) => setGetStaffId(staff)}
            />
          )}
        </div>
      </FormControl>
      <Button
        disabled={isCreateLoading || isUpdateLoading || !isValid}
        type="submit"
        className="w-full"
        variant="contained"
        color="secondary"
      >
        <span className="mx-8">{t('Save')}</span>
      </Button>
    </form>
  );
};

export default TagsForm;
